import React, { useState, useEffect, useCallback } from 'react';
import { Store, RefreshCw } from 'lucide-react';
import apiService from '../apiService';
import { useAdmin } from '../context/AdminContext';
import Img from '../components/Img';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';

// Libellés des types de commerce (admin, français)
const TYPE_LABELS = {
  restaurant: 'Restaurant', supermarket: 'Supermarché', pharmacy: 'Pharmacie', fuel: 'Carburant', shop: 'Boutique',
  bakery: 'Boulangerie', butcher: 'Boucherie', bar: 'Bar', cafe: 'Café', hotel: 'Hôtel', laundry: 'Pressing',
  beauty_salon: 'Institut de beauté', gym: 'Salle de sport', electronics: 'Électronique', clothing: 'Vêtements',
  bookstore: 'Librairie', hardware: 'Quincaillerie', florist: 'Fleuriste', vehicle_rental: 'Location de véhicules', other: 'Autre',
};
const MODE_META = {
  auto: { label: 'Horaires auto', color: '#166534', bg: '#ECFDF5' },
  manual: { label: 'Ouvert manuellement', color: '#C9350F', bg: '#FEE9E2' },
  on_duty: { label: '🌙 De garde', color: '#5B21B6', bg: '#EDE9FE' },
};
const REFRESH_MS = 60000;

export default function OpenBusinessesPage() {
  const { setCurrentPage, setSelectedSupplierId } = useAdmin();
  const [data, setData] = useState({ businesses: [], count: 0, total: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes
  const cities = useCities();

  const load = useCallback(async () => {
    try {
      const res = await apiService.get('/admin/suppliers/open', { userType: 'admin' });
      if (res?.success) { setData(res); setUpdatedAt(new Date()); }
    } catch (err) { console.error('Erreur business ouverts:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const inCity = data.businesses.filter(b => !cityFilter || b.city_slug === cityFilter);
  const visible = inCity.filter(b => !typeFilter || b.business_type === typeFilter);
  const typesInCity = Object.entries(inCity.reduce((acc, b) => { acc[b.business_type] = (acc[b.business_type] || 0) + 1; return acc; }, {}))
    .sort((a, b) => b[1] - a[1]);

  const openSupplier = (b) => { setSelectedSupplierId(b.supplier_id); setCurrentPage('delivery-suppliers'); };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Store size={28} /> Business ouverts <span className="dash-live-dot" />
        </h1>
        <div className="page-actions">
          <CityFilter value={cityFilter} onChange={setCityFilter} cities={cities} />
          <button className="btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshCw size={16} /> Actualiser
          </button>
        </div>
      </div>
      <p style={{ margin: '-0.5rem 0 1rem', fontSize: '0.8rem', color: '#9AA0A8' }}>
        {inCity.length} ouvert{inCity.length > 1 ? 's' : ''} maintenant sur {cityFilter ? 'les commerces de cette ville' : `${data.total} commerces approuvés`} · même règle que le Market (horaires à l'heure locale de la ville, statut manuel, pharmacies de garde) · rafraîchi chaque minute{updatedAt ? ` · ${updatedAt.toLocaleTimeString('fr-FR')}` : ''}
      </p>

      {/* Filtre par type */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button onClick={() => setTypeFilter('')}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', background: !typeFilter ? '#F54119' : '#F4F5F7', color: !typeFilter ? 'white' : '#5C636E' }}>
          Tous ({inCity.length})
        </button>
        {typesInCity.map(([type, n]) => (
          <button key={type} onClick={() => setTypeFilter(type)}
            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '0.8rem', background: typeFilter === type ? '#F54119' : '#F4F5F7', color: typeFilter === type ? 'white' : '#5C636E' }}>
            {TYPE_LABELS[type] || type} ({n})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th>Commerce</th><th>Type</th><th>Ville</th><th>Ouverture</th><th>Horaires du jour</th><th>Téléphone</th></tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#9AA0A8' }}>Aucun commerce ouvert{cityFilter ? ` à ${cityLabel(cities, cityFilter)}` : ''} en ce moment</td></tr>
              )}
              {visible.map(b => {
                const m = MODE_META[b.mode] || MODE_META.manual;
                return (
                  <tr key={b.supplier_id}>
                    <td>
                      <div className="driver-cell" onClick={() => openSupplier(b)} style={{ cursor: 'pointer' }}>
                        {b.logo ? <Img src={b.logo} thumb={b.logo_thumb} alt={b.business_name} className="table-avatar" /> : <div className="table-avatar no-photo">{b.business_name?.charAt(0)}</div>}
                        <span style={{ textDecoration: 'underline', color: '#C9350F', fontWeight: '500' }}>{b.business_name}</span>
                      </div>
                    </td>
                    <td>{TYPE_LABELS[b.business_type] || b.business_type}</td>
                    <td>{cityLabel(cities, b.city_slug)}</td>
                    <td><span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{m.label}</span></td>
                    <td style={{ fontSize: '0.85rem', color: '#414852', whiteSpace: 'nowrap' }}>{b.today_hours ? `${b.today_hours.open} – ${b.today_hours.close}` : '—'}</td>
                    <td>{b.phone ? <a href={`tel:${b.phone}`} style={{ color: '#C9350F' }}>{b.phone}</a> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
